/**
 * Every URL the marketing site owns.
 *
 * This is the list `prerender.mjs` walks to write static HTML and the list the client router
 * matches on hydration. It was once half of one table shared with the tools; the split is what
 * lets landing build, deploy and be reasoned about on its own.
 *
 * `title` and `description` stay per-URL for the reason they always were: a crawler needs one URL
 * per subject and a document it can read without executing anything, and twenty pages sharing one
 * title is one page as far as a search index is concerned.
 *
 * THE PATHS ARE AT THE ROOT, not under a `/landing` prefix, because landing and gtm ship to one
 * origin. See GTM_PREFIXES in @agitome/ui-shared for what that costs and how dev pays it.
 */
import { matchIn, type Route } from "@agitome/ui-shared"
import * as Landing from "./pages"
import * as Company from "./company"

export type { Route }

// Re-exported so the prerenderer can reach it through the SSR bundle of this file. It seeds the
// preload cache before each page renders, which is what puts the fetched content INTO the HTML
// rather than leaving "Loading…" for a crawler to read.
export { seedPreload } from "@agitome/ui-shared"

export const ROUTES: Route[] = [
  { path: "/", title: "agitome — the risk layer for the agentic internet",
    description: "Before you ship on a framework, a model, or somebody else's agent — see what has actually gone wrong with it. Free, no account, and every claim is answerable by the party it names.",
    element: Landing.Home, loads: { drop: "/state-of-agent-risk" } },
  { path: "/how", title: "How agitome works — two corpora, one of them evidence",
    description: "The registry is what strangers reported. The record is what a tenant signed. A report becomes evidence only by being adjudicated against a signed loss event.",
    element: Landing.How },
  { path: "/principles", title: "What agitome won't do",
    description: "No rankings, no incident rate, no prices, no hidden claims, no silent scoring, no reported-as-verified. The refusals, and why each is load-bearing.",
    element: Landing.Principles },
  { path: "/pricing", title: "Pricing — looking is free forever",
    description: "Every one of the twelve tools stays free and needs no account. What costs money is what a free lookup cannot be: continuous, enforced in your pipeline, answerable to a deadline.",
    element: Landing.Pricing },
  { path: "/partners", title: "Founding partners — ten places",
    description: "Ten teams running agitome against a real stack and telling us where the taxonomy is wrong. Everything free for the term; a reference is not part of the exchange.",
    element: Landing.Partners },

  // ── the pages that make it read as a company that exists ─────────────────
  { path: "/faq", title: "Questions",
    description: "Is a report a finding? Can I get an incident taken down? Why is there no incident rate? The awkward ones, answered.",
    element: Company.Faq },
  { path: "/changelog", title: "Changelog",
    description: "What changed in agitome, and when.",
    element: Company.Changelog },
  { path: "/privacy", title: "Privacy",
    description: "What agitome holds, what it refuses to hold, and how to leave.",
    element: Company.Privacy },
  { path: "/terms", title: "Terms",
    description: "What the registry is, and what it is not.",
    element: Company.Terms },
  { path: "/contact", title: "Talk to us — agitome",
    description: "A question about the registry, a correction, or a page about your product you want to answer. A person reads these.",
    element: Company.ContactPage },
]

export function match(pathname: string): Route | undefined {
  return matchIn(ROUTES, pathname)
}
